import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import { TextField } from '@material-ui/core';

// Redux
import { connect } from 'react-redux';
import { clearErrors, setAlert, setSuccess } from '../../../redux/actions/alertActions';

// Components
import AdminTable, { primaryButton } from '../../../components/AdminTable';
import CrudDialog from '../../../components/CrudDialog';

// API
import { listRedAlertTextGroups, deleteRedAlertTextContact, updateRedAlertTextGroup } from '../../../api';

// Constants
import { T, FONT_MONO } from '../../../constants/tokens';

const COLUMNS = [
  { label: 'Name', width: 'minmax(170px, 1fr)', minWidth: 170, render: (g) => g.getName() },
  { label: 'Recipients', width: '110px', minWidth: 110, render: (g) => String(g.getContacts()?.length ?? 0) },
  { label: 'Message', width: 'minmax(220px, 2fr)', minWidth: 220, render: (g) => g.getMessage() }
];

const smallButton = (tone) => ({
  fontFamily: 'inherit',
  fontSize: 14,
  fontWeight: 500,
  lineHeight: '20px',
  padding: '6px 12px',
  borderRadius: 6,
  border: '1px solid ' + T.hairline,
  background: T.elevated,
  color: tone === 'danger' ? '#ee0000' : T.ink,
  cursor: 'pointer'
});

/**
 * The red alert text groups: who is texted when the school goes into
 * Emergency, and what they are sent. The groups themselves are fixed; each
 * opens to edit its message and its contacts.
 */
class RedAlertTextGroups extends Component {
  constructor(props) {
    super(props);
    this.table = React.createRef();
    this.state = { group: null, message: '', isLoading: false };
  }

  componentWillUnmount() {
    this.props.clearErrors();
  }

  open = (group) => this.setState({ group, message: group.getMessage() ?? '' });

  /** Reload, and keep the open group current. */
  refresh = () => {
    this.table.current?.reload();
    if (this.state.group == null) return;
    listRedAlertTextGroups().then((groups) => {
      const group = groups.find((g) => g.getID() === this.state.group?.getID());
      if (group) this.setState({ group });
    }).catch(() => {});
  }

  saveMessage = () => {
    this.setState({ isLoading: true });
    updateRedAlertTextGroup(this.state.group.getID(), { message: this.state.message }).then(() => {
      this.setState({ isLoading: false });
      this.props.setSuccess('Red alert text message updated');
      this.refresh();
    }).catch(error => {
      this.setState({ isLoading: false });
      this.props.setError(error ?? 'Unable to update red alert text message');
    });
  }

  deleteContact = (contact) => {
    deleteRedAlertTextContact(contact.getID()).then(() => {
      this.props.setSuccess(contact.getName() + ' removed.');
      this.refresh();
    }).catch(error => {
      this.props.setError(error ?? 'Unable to remove this contact');
    });
  }

  render() {
    const { group } = this.state;
    const contacts = group?.getContacts() ?? [];
    const unchanged = group != null && this.state.message === (group.getMessage() ?? '');
    const saveDisabled = this.state.isLoading || unchanged;

    return (
      <>
        <CrudDialog
          open={group != null}
          onClose={() => this.setState({ group: null })}
          eyebrow='Red alert texts'
          title={group?.getName() ?? ''}
          width={720}
        >
          {group != null &&
            <div style={{ paddingTop: 16 }}>
              <div style={{ fontFamily: FONT_MONO, fontSize: 12, fontWeight: 500, textTransform: 'uppercase', color: T.body, marginBottom: 8 }}>Message</div>
              <TextField
                label='Text message content'
                value={this.state.message}
                onChange={(e) => this.setState({ message: e.target.value })}
                disabled={this.state.isLoading}
                variant='outlined'
                multiline
                minRows={3}
                fullWidth
              />
              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12, marginTop: 8 }}>
                <span style={{ flex: 1, minWidth: 200, fontSize: 13, color: T.body }}>Include the school name in every message.</span>
                <button
                  type='button'
                  style={{ ...primaryButton, opacity: saveDisabled ? 0.5 : 1, cursor: saveDisabled ? 'default' : 'pointer' }}
                  disabled={saveDisabled}
                  onClick={this.saveMessage}
                >
                  Save message
                </button>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 32, marginBottom: 8 }}>
                <span style={{ flex: 1, fontFamily: FONT_MONO, fontSize: 12, fontWeight: 500, textTransform: 'uppercase', color: T.body }}>
                  Recipients · {contacts.length}
                </span>
                <button type='button' style={smallButton()} onClick={() => this.props.history.push('/admin/red-alert-text-groups/' + group.getID() + '/add')}>
                  Add contact
                </button>
              </div>
              {contacts.length === 0 &&
                <div style={{ padding: '12px 0', fontSize: 14, color: T.muted }}>No one is texted for this group yet.</div>}
              {contacts.map((contact) => (
                <div key={contact.getID()} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: '1px solid ' + T.hairlineSoft }}>
                  <span style={{ flex: '1 1 160px', minWidth: 0, fontSize: 14, color: T.ink }}>{contact.getName()}</span>
                  <span style={{ flex: '0 0 150px', fontFamily: FONT_MONO, fontSize: 13, color: T.body }}>{contact.getPhoneNumber()}</span>
                  <button type='button' style={smallButton('danger')} onClick={() => this.deleteContact(contact)}>Remove</button>
                </div>
              ))}
            </div>}
        </CrudDialog>

        <AdminTable
          ref={this.table}
          title='Red alert texts'
          singular='text group'
          columns={COLUMNS}
          load={listRedAlertTextGroups}
          idOf={(g) => g.getID()}
          searchText={(g) => [g.getName(), ...(g.getContacts() ?? []).map((c) => c.getName())].filter(Boolean).join(' ')}
          searchPlaceholder='Search group or contact'
          rowActions={() => [{ label: 'Edit', onClick: this.open }]}
        />
      </>
    );
  }
}

const mapDispatchToProps = dispatch => ({
  setSuccess: (msg) => setSuccess(dispatch, msg),
  setError: (msg) => {
    dispatch(clearErrors());
    setAlert(dispatch, msg);
  },
  clearErrors: () => dispatch(clearErrors())
});

RedAlertTextGroups.propTypes = {
  history: PropTypes.object.isRequired,
  setError: PropTypes.func.isRequired,
  clearErrors: PropTypes.func.isRequired,
  setSuccess: PropTypes.func.isRequired
};

export default connect(null, mapDispatchToProps)(withRouter(RedAlertTextGroups));
